import { SmsFailed } from "@mui/icons-material"
import { Box, Fade, Tooltip, Typography } from "@mui/material"
import { t } from "i18next"
import { FC, useEffect, useState } from "react"

/**
 * The door-tile SMS status indicator: when a reservation's SMS failed, the tile
 * alternates between the door label and a red SmsFailed icon whose tooltip says
 * what went wrong.
 *
 * Extracted from OsLockerDoor (LOCKER-401) for two reasons: that file is over
 * the ~400-line limit and the ticket forbids growing it, and the tooltip text
 * needed to be translated — which is easier to see is correct in a small file
 * than buried 360 lines into a large one.
 *
 * The tooltip never renders the provider's raw error string. It embeds the
 * tenant's Twilio account SID, so the server strips it and sends the boolean
 * `hasDeliveryError` in its place.
 */

const enum TwilioStatusEnum {
  delivered_unknown = "delivered_unknown",
  undelivered = "undelivered",
  failed = "failed",
}

const getSmsStatusColor = (status?: string): string => {
  switch (status) {
    case TwilioStatusEnum.delivered_unknown:
    case TwilioStatusEnum.undelivered:
    case TwilioStatusEnum.failed:
      return "red"
    default:
      return "grey"
  }
}

const getSmsStatusText = (status?: string): string => {
  switch (status) {
    case TwilioStatusEnum.delivered_unknown:
      return t("smsStatusDeliveryUnknown")
    case TwilioStatusEnum.undelivered:
      return t("smsStatusUndelivered")
    case TwilioStatusEnum.failed:
      return t("smsStatusFailed")
    default:
      return t("smsStatusUnknown")
  }
}

/** True when the door's SMS is in any state the operator should be alerted to. */
export const hasSmsErrorFor = (
  smsTwilioStatus?: string,
  hasSmsDeliveryError?: boolean,
): boolean =>
  smsTwilioStatus === TwilioStatusEnum.delivered_unknown ||
  smsTwilioStatus === TwilioStatusEnum.undelivered ||
  smsTwilioStatus === TwilioStatusEnum.failed ||
  !!hasSmsDeliveryError

type OsLockerDoorSmsIndicatorProps = {
  doorLabel: string
  statusTextColor?: string
  smsTwilioStatus?: string
  hasSmsDeliveryError?: boolean
}

const badgeSx = {
  position: "absolute" as const,
  top: 0,
  left: 0,
  width: 23,
  height: 23,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
}

export const OsLockerDoorSmsIndicator: FC<OsLockerDoorSmsIndicatorProps> = ({
  doorLabel,
  statusTextColor,
  smsTwilioStatus,
  hasSmsDeliveryError = false,
}) => {
  const hasSmsError = hasSmsErrorFor(smsTwilioStatus, hasSmsDeliveryError)

  const [showNumber, setShowNumber] = useState(true)

  useEffect(() => {
    if (!hasSmsError) {
      return
    }
    const interval = setInterval(() => setShowNumber((prev) => !prev), 1000)
    return () => clearInterval(interval)
  }, [hasSmsError])

  const label = (
    <Typography variant="caption">{doorLabel}</Typography>
  )

  if (!hasSmsError) {
    return (
      <Box
        sx={{
          borderRadius: "50%",
          width: 23,
          height: 23,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          border: "1px solid ",
          borderColor: statusTextColor,
        }}
      >
        {label}
      </Box>
    )
  }

  return (
    <Box sx={{ position: "relative", py: "14px" }}>
      <Fade in={showNumber} timeout={500}>
        <Box
          sx={{
            ...badgeSx,
            borderRadius: "50%",
            border: "1px solid ",
            borderColor: statusTextColor,
          }}
        >
          {label}
        </Box>
      </Fade>
      <Fade in={!showNumber} timeout={500}>
        <Box sx={badgeSx}>
          <Tooltip
            title={
              hasSmsDeliveryError
                ? t("smsDeliveryFailedGeneric")
                : getSmsStatusText(smsTwilioStatus)
            }
          >
            <SmsFailed
              sx={{
                fontSize: 16,
                color: hasSmsDeliveryError
                  ? "red"
                  : getSmsStatusColor(smsTwilioStatus),
              }}
            />
          </Tooltip>
        </Box>
      </Fade>
    </Box>
  )
}
